import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, ComponentSync, Label, LL_H_C, LL_V_L, openContent, TS_Input, TS_PropRenderer, TS_Tabs, VirtualizedList } from '@nu-art/thunderstorm-frontend/index';
import { Component_ContextRule } from './Component_ContextRule.js';
import './Component_ContextRules.scss';
import { arrayIncludesAll, dbObjectToId, Filter, filterDuplicates, generateHex, logicalXOR, sortArray, toggleElementInArray } from '@nu-art/ts-common';
import { ICONS } from '@app/styles';
import { ModuleFE_Variable } from '../../_entity/variable/index.js';
import { QScrollWrapper } from '../../index.js';
export class Component_ContextRules extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.editableContextRules = nextProps.editableContextRules;
        state.editableExcludedVariables = nextProps.editableExcludedVariables;
        state.editMode = !!nextProps.editMode;
        state.focusedRuleIndexes ??= [];
        state.aggregatedVariables = this.getAggregatedVariables(state, state.searchTerm);
        return state;
    }
    //######################### Logic #########################
    getFocusedRules(state) {
        const rules = state.editableContextRules.item;
        const focusedIndexes = state.focusedRuleIndexes;
        if (!focusedIndexes.length)
            return rules;
        return rules.filter((rule, index) => focusedIndexes.includes(index));
    }
    getAggregatedVariables(state, searchTerm) {
        const rules = this.getFocusedRules(state);
        let variables = rules.map(rule => this.getVariablesForRule(rule)).flat();
        //Filter duplicates
        variables = filterDuplicates(variables, dbObjectToId);
        //Filter variables that are excluded
        variables = variables.filter(item => !state.editableExcludedVariables.item.includes(item._id));
        //Filter by search term
        if (searchTerm && searchTerm.length >= 3) {
            const filter = new Filter(v => [v.name]);
            variables = filter.filter(variables, searchTerm);
        }
        //Sort
        variables = sortArray(variables, v => v._nameInLowercase);
        return variables;
    }
    getVariablesForRule(rule) {
        return ModuleFE_Variable.cache.filter(variable => {
            if (rule.variableTypes?.length && !rule.variableTypes.includes(variable.type))
                return false;
            return arrayIncludesAll(variable._tagIds, rule.tagIds);
        });
    }
    getExcludedVariables = () => {
        const excludedVariableIds = this.state.editableExcludedVariables.item;
        let variables = ModuleFE_Variable.cache.filter(variable => excludedVariableIds.includes(variable._id));
        //Filter by search term
        if (this.state.searchTerm) {
            const filter = new Filter(v => [v.name]);
            variables = filter.filter(variables, this.state.searchTerm);
        }
        //Sort
        variables = sortArray(variables, v => v._nameInLowercase);
        return variables;
    };
    deleteRule = async (index) => {
        //If no need to remove from focused rules
        if (!this.state.focusedRuleIndexes.includes(index)) {
            await this.state.editableContextRules.removeArrayItem(index);
            return;
        }
        //Remove from focused rules and delete rule
        return new Promise((resolve, reject) => {
            const focusedRuleIndexes = this.state.focusedRuleIndexes.filter(i => i !== index);
            this.setState({ focusedRuleIndexes }, async () => {
                try {
                    await this.state.editableContextRules.removeArrayItem(index);
                    resolve();
                }
                catch (err) {
                    reject(err);
                }
            });
        });
    };
    toggleRuleFocus = (index) => {
        const focusedRuleIndexes = [...this.state.focusedRuleIndexes];
        toggleElementInArray(focusedRuleIndexes, index);
        const newState = { ...this.state, focusedRuleIndexes };
        this.setState({ focusedRuleIndexes, aggregatedVariables: this.getAggregatedVariables(newState, this.state.searchTerm) });
    };
    getToolTip = (text) => {
        return openContent.tooltip.top('context-rules__tooltip', text, { offset: 10 });
    };
    onSearch = (term) => {
        //New term passes min length
        if (term.length >= 3)
            return this.setState({ searchTerm: term, aggregatedVariables: this.getAggregatedVariables(this.state, term) });
        //New term does not pass min length, only re-calc variables if the last term did pass.
        const prevTerm = this.state.searchTerm ?? '';
        if (prevTerm.length >= 3)
            return this.setState({ searchTerm: term, aggregatedVariables: this.getAggregatedVariables(this.state) });
        //Only set the search term
        this.setState({ searchTerm: term });
    };
    toggleVariableExclusion = (variableId, exclude) => {
        const isVariableExcluded = this.state.editableExcludedVariables.item.includes(variableId);
        if (!logicalXOR(isVariableExcluded, exclude))
            return;
        //Exclude variable
        if (exclude)
            return this.state.editableExcludedVariables.updateArrayAt(variableId);
        //Un-exclude variable
        const index = this.state.editableExcludedVariables.item.indexOf(variableId);
        this.state.editableExcludedVariables.removeArrayItem(index);
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_H_C, { className: 'context-rules', children: [this.renderRules(), this.renderRelatedVariables()] });
    }
    renderRules = () => {
        const rules = this.state.editableContextRules;
        return _jsx(TS_PropRenderer.Vertical, { label: 'Rules', className: 'context-rules__rules', children: _jsxs(QScrollWrapper, { children: [rules.item.map((rule, index) => {
                        const editable = rules.editProp(index, {});
                        return _jsx(Component_ContextRule
                        //This is a prime example of where using a random key is okay.
                        //since we are in a map func, we must provide a key, but using the index
                        //Will create bugs when removing rules, so a random hex is fine here.
                        , { editable: editable, ruleIndex: index, editMode: this.state.editMode, onRuleDelete: async () => this.deleteRule(index), focused: this.state.focusedRuleIndexes.includes(index), onRuleFocus: () => this.toggleRuleFocus(index) }, generateHex(4));
                    }), this.state.editMode &&
                        _jsx(Button, { variant: 'primary', onClick: async () => rules.updateArrayAt({ tagIds: [] }), children: "Add Rule" })] }) });
    };
    renderRelatedVariables = () => {
        const label = () => {
            const tooltipContent = this.state.focusedRuleIndexes.length
                ? `Variables that pass at least one of the following rules: ${sortArray(this.state.focusedRuleIndexes).map(index => `Rule ${index + 1}`).join(', ')}`
                : 'Variables that pass at lease one rule';
            return _jsxs(_Fragment, { children: [`Variable Aggregation
			(${this.state.aggregatedVariables.length})`, _jsx(ICONS.information.component, { style: { cursor: 'help' }, ...this.getToolTip(tooltipContent) })] });
        };
        const tabs = [{
                uid: '0',
                content: () => this.renderTab_VariableAggregation(),
                title: label,
            }, {
                uid: '1',
                content: () => this.renderTab_ExcludedVariables(),
                title: 'Excluded Variables',
            }];
        //Return Tabs, one for Variables, one for excluded variables
        return _jsx(TS_Tabs, { className: 'context-rules__related-variables', tabs: tabs });
    };
    renderTab_VariableAggregation = () => {
        return _jsxs(LL_V_L, { className: 'tab__variable-aggregation', children: [_jsx(TS_Input, { type: 'text', value: this.state.searchTerm, onChange: this.onSearch, placeholder: 'Enter search term' }), _jsx(VirtualizedList, { className: 'context-rules__related-variables__list', listToRender: this.state.aggregatedVariables.map(variable => this.render_Variable(variable, () => this.toggleVariableExclusion(variable._id, true))), height: 850, itemHeight: 30 })] });
    };
    renderTab_ExcludedVariables = () => {
        const variables = this.getExcludedVariables();
        return _jsxs(LL_V_L, { className: 'tab__excluded-variables', children: [_jsx(TS_Input, { type: 'text', value: this.state.searchTerm, onChange: this.onSearch, placeholder: 'Enter search term' }), _jsx(VirtualizedList, { className: 'context-rules__related-variables__list', listToRender: variables.map(variable => this.render_Variable(variable, () => this.toggleVariableExclusion(variable._id, false))), height: 850, itemHeight: 30 })] });
    };
    render_Variable = (variable, onIconClick) => {
        return _jsxs(LL_H_C, { className: 'variable', children: [_jsx(Label, { tooltip: variable.name, containerSelector: '.context-rules__related-variables__list', children: variable.name }), this.state.editMode && _jsx(ICONS.x.component, { onClick: onIconClick })] }, variable._id);
    };
}
//# sourceMappingURL=Component_ContextRules.js.map